"use client"

import { useState, useMemo, useEffect, useContext } from "react"
import { useLocation } from "react-router-dom"
import ProductCard from "../components/ProductCard"
import { FilterSidebar } from "../components/FilterSidebar"
// import { SortDropdown } from "../components/SortDropdown" // Not currently used
import { ProductPagination } from "../components/ProductPagination"
// import { Button } from "../components/ui/button" // Not currently used
import { Input } from "../components/ui/input"
import { Search, X } from "lucide-react"
import { filterGroups } from "../data/products"
// import { sortOptions } from "../data/products" // Not currently used
// import { useDispatch } from "react-redux" // Not currently used
import addToCart from "../helpers/addToCart"
import summaryApi from "../common/index"
import Context from "../context"
import { toast } from "react-hot-toast"
import Sidebar from "../components/sidebar"

const PRODUCTS_PER_PAGE = 8

export default function ProductsPage() {
  const location = useLocation()
  const passedProducts = location.state?.products || null
  const audienceTitle = location.state?.audience || "All"

  // const dispatch = useDispatch() // Not currently used

  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [selectedFilters, setSelectedFilters] = useState({})
  const [selectedSort] = useState("featured") // setSelectedSort not used
  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState("")

  const [products, setProducts] = useState(passedProducts || [])
  const [loadingProducts, setLoadingProducts] = useState(!passedProducts)

  const [wishlistedItems, setWishlistedItems] = useState(new Set())
  // const [loadingWishlist, setLoadingWishlist] = useState(false) // loadingWishlist not used, only setter
  // const [addingToCart, setAddingToCart] = useState(false) // addingToCart not used, only setter
  const { fetchUserAddToCart, fetchUserWishlistCount, openCartSidebar, isCartOpen, closeCartSidebar } =
    useContext(Context)
  // const [size, setSize] = useState("") // Not currently used

  useEffect(() => {
    const fetchWishlistData = async () => {
      // setLoadingWishlist(true) // Not currently used
      try {
        const res = await fetch(summaryApi.getWishlistProduct.url, {
          method: summaryApi.getWishlistProduct.method,
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        })
        const data = await res.json()
        if (data.success && data.data) {
          const wishlistIds = new Set(data.data.map((item) => item._id))
          setWishlistedItems(wishlistIds)
        }
      } catch (err) {
        console.log("Failed to load wishlist")
      } finally {
        // setLoadingWishlist(false) // Not currently used
      }
    }

    fetchWishlistData()
  }, [])

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const audience = urlParams.get("audience")
    const section = urlParams.get("section")
    const category = urlParams.get("category")
    const subcategory = urlParams.get("subcategory")

    // If URL has filter parameters, fetch filtered products
    if (audience || section || category || subcategory) {
      fetchFilteredProducts({ audience, section, category, subcategory })
    } else if (!passedProducts) {
      fetchProducts()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, passedProducts])

  const fetchFilteredProducts = async (filters) => {
    setLoadingProducts(true)
    try {
      const params = new URLSearchParams()
      if (filters.audience) params.append("audience", filters.audience)
      if (filters.section) params.append("section", filters.section)
      if (filters.category) params.append("category", filters.category)
      if (filters.subcategory) params.append("subcategory", filters.subcategory)

      const res = await fetch(`${summaryApi.getFilteredProducts.url}?${params.toString()}`, {
        method: summaryApi.getFilteredProducts.method,
        headers: { "Content-Type": "application/json" },
      })
      const data = await res.json()
      if (data.success) {
        setProducts(data.data || [])
        console.log("data filtered:",data)
      } else {
        toast.error("Failed to load filtered products")
      }
    } catch (err) {
      toast.error("Failed to load products")
    } finally {
      setLoadingProducts(false)
    }
  }

  const fetchProducts = async () => {
    if (passedProducts) return

    try {
      const res = await fetch(summaryApi.allProduct.url, {
        method: summaryApi.allProduct.method,
        headers: { "Content-Type": "application/json" },
      })
      const data = await res.json()
      if (data.success) {
        setProducts(data.data || [])
      }
    } catch (err) {
      toast.error("Failed to load products")
    } finally {
      setLoadingProducts(false)
    }
  }

  const filteredAndSortedProducts = useMemo(() => {
    let filtered = [...products]

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim()
      filtered = filtered.filter((p) => {
        if (!p) return false

        const nameMatch = String(p.productName || "")
          .toLowerCase()
          .includes(query)
        const categoryMatch = String(p.category || "")
          .toLowerCase()
          .includes(query)
        const brandMatch = String(p.brandName || "")
          .toLowerCase()
          .includes(query)
        const descriptionMatch = String(p.description || "")
          .toLowerCase()
          .includes(query)

        return nameMatch || categoryMatch || brandMatch || descriptionMatch
      })
    }

    // Apply filters
    Object.entries(selectedFilters).forEach(([groupId, options]) => {
      if (options.length > 0) {
        filtered = filtered.filter((p) => {
          if (!p) return false
          switch (groupId) {
            case "category":
              return options.includes(String(p.category || ""))
            case "size":
              return (p.sizes || []).some((s) => options.includes(String(s.size || "")))
            case "color":
              return options.some((c) =>
                String(p.color || "")
                  .toLowerCase()
                  .includes(String(c).toLowerCase()),
              )
            case "material":
              return options.some((m) =>
                (p.materials || []).some((mat) =>
                  String(mat || "")
                    .toLowerCase()
                    .replace(" ", "-")
                    .includes(String(m).toLowerCase()),
                ),
              )
            default:
              return true
          }
        })
      }
    })

    // Apply sorting
    switch (selectedSort) {
      case "price-low-high":
        filtered.sort((a, b) => (a.priceRs || 0) - (b.priceRs || 0))
        break
      case "price-high-low":
        filtered.sort((a, b) => (b.priceRs || 0) - (a.priceRs || 0))
        break
      case "newest":
        filtered.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0))
        break
      case "name-a-z":
        filtered.sort((a, b) => String(a.productName || "").localeCompare(String(b.productName || "")))
        break
      case "name-z-a":
        filtered.sort((a, b) => String(b.productName || "").localeCompare(String(a.productName || "")))
        break
      default:
        break
    }

    return filtered
  }, [products, selectedFilters, selectedSort, searchQuery])

  const getPageTitle = () => {
    const urlParams = new URLSearchParams(location.search)
    const audience = urlParams.get("audience")
    const category = urlParams.get("category")
    const subcategory = urlParams.get("subcategory")

    if (subcategory && category) {
      return `${audience ? audience.toUpperCase() : "ALL"} | ${category.toUpperCase()} - ${subcategory.toUpperCase()}`
    } else if (category) {
      return `${audience ? audience.toUpperCase() : "ALL"} | ${category.toUpperCase()}`
    } else if (audience) {
      return `${audience.toUpperCase()}`
    }
    return audienceTitle.toUpperCase()
  }

  const handleFilterChange = (groupId, optionId, checked) => {
    setSelectedFilters((prev) => {
      const groupOptions = prev[groupId] || []
      const newOptions = checked ? [...groupOptions, optionId] : groupOptions.filter((id) => id !== optionId)
      return { ...prev, [groupId]: newOptions }
    })
    setCurrentPage(1)
  }

  const handleClearAllFilters = () => {
    setSelectedFilters({})
    setSearchQuery("")
    setCurrentPage(1)
  }

  // const handleSortChange = (value) => {
  //   setSelectedSort(value)
  //   setCurrentPage(1)
  // }

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value)
    setCurrentPage(1)
  }

  const handleClearSearch = () => {
    setSearchQuery("")
    setCurrentPage(1)
  }

  const handleWishlistToggle = async (productId) => {
    if (!productId) return
    const isWishlisted = wishlistedItems.has(productId)

    try {
      if (isWishlisted) {
        const res = await fetch(summaryApi.removeWishlistProduct.url, {
          method: summaryApi.removeWishlistProduct.method,
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ productId }),
        })
        const data = await res.json()
        if (data.success) {
          setWishlistedItems((prev) => {
            const newSet = new Set(prev)
            newSet.delete(productId)
            return newSet
          })
          toast.success("Product removed from wishlist!")
          await fetchUserWishlistCount()
        }
      } else {
        const res = await fetch(summaryApi.addToWishlist.url, {
          method: summaryApi.addToWishlist.method,
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ productId }),
        })
        const data = await res.json()
        if (data.success) {
          setWishlistedItems((prev) => new Set(prev).add(productId))
          toast.success("Product added to wishlist!")
          await fetchUserWishlistCount()
        }
      }
    } catch (err) {
      toast.error("Failed to add to wishlist.")
    }
  }

 const handleAddToCart = async (e, id) => {
  // setAddingToCart(true); // Not currently used

  try {
    const result = await addToCart(e, id); // no size parameter

    if (result.success) {
      await fetchUserAddToCart();
      openCartSidebar();
    }
  } catch (error) {
    toast.error("Failed to add product to cart");
  } finally {
    // setAddingToCart(false); // Not currently used
  }
};


  const totalPages = Math.ceil(filteredAndSortedProducts.length / PRODUCTS_PER_PAGE)
  const startIndex = (currentPage - 1) * PRODUCTS_PER_PAGE
  const paginatedProducts = filteredAndSortedProducts.slice(startIndex, startIndex + PRODUCTS_PER_PAGE)

if (loadingProducts) {
  return (
  <div className="fixed inset-0 flex flex-col justify-center items-center bg-black bg-opacity-70 z-50">
  <div className="sparkle-loader relative mb-4">
    <div className="golden-rings">
      <span></span>
      <span></span>
      <span></span>
    </div>
    <div className="sparkles">
      <div className="sparkle"></div>
      <div className="sparkle"></div>
      <div className="sparkle"></div>
      <div className="sparkle"></div>
    </div>
  </div>
  <p className="loader-text text-xl font-bold text-yellow-400 animate-pulse">
    Loading… ✨
  </p>
</div>

  );
}



  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full max-w-full mx-auto py-8 px-2 md:px-6 lg:px-8">
        {/* Enhanced Header Section */}
        <div className="bg-[#8B4513] rounded-2xl shadow-xl overflow-hidden mb-10 md:mb-12">
          <div className="relative px-6 md:px-10 lg:px-16 py-8 md:py-12">
            {/* Decorative Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
              <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/2 translate-y-1/2"></div>
            </div>
            
            {/* Content */}
            <div className="relative z-10 text-center">
              {/* Breadcrumb/Subtitle */}
              <div className="flex items-center justify-center gap-2 mb-4">
                <span className="text-white/80 text-sm md:text-base font-medium">Premium Collection</span>
                <span className="text-white/60">•</span>
                <span className="text-white/80 text-sm md:text-base font-medium">{getPageTitle()}</span>
              </div>
              
              {/* Main Title */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 tracking-tight">
                Premium Saudi Dates & Attar
              </h1>
              
              {/* Divider */}
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="h-0.5 w-16 bg-white/40"></div>
                <div className="w-2 h-2 bg-white rounded-full"></div>
                <div className="h-0.5 w-16 bg-white/40"></div>
              </div>
              
              {/* Product Count Badge */}
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full border border-white/30">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                <span className="text-white font-semibold text-base md:text-lg">
                  {filteredAndSortedProducts.length} Product{filteredAndSortedProducts.length !== 1 ? "s" : ""} Available
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col md:flex-row gap-6">
          <div
            className={`flex-shrink-0 transition-all duration-300 ease-in-out ${isFilterOpen ? "w-64" : "w-0"} overflow-hidden`}
          >
            <FilterSidebar
              isOpen={isFilterOpen}
              onToggle={() => setIsFilterOpen(!isFilterOpen)}
              filters={filterGroups}
              selectedFilters={selectedFilters}
              onFilterChange={handleFilterChange}
              onClearAll={handleClearAllFilters}
            />
          </div>

          <div className="flex-1 transition-all duration-300">
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="pl-10 pr-10 py-2 w-full border-gray-300 focus:border-gray-500 focus:ring-gray-500"
                />
                {searchQuery && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="flex items-center gap-2 border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
              >
                Filter
              </Button>

              <SortDropdown options={sortOptions} selectedSort={selectedSort} onSortChange={handleSortChange} />
            </div> */}

            <div
              className={`grid gap-4 md:gap-6 transition-all duration-300 ${
                isFilterOpen ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3" : "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
              }`}
            >
             {paginatedProducts
  .filter((p) => p && p._id)
  .map((product, index) => (
    <ProductCard
      key={product._id}
      product={product}
      isWishlisted={wishlistedItems.has(product._id)}
      onWishlistToggle={handleWishlistToggle}
      onAddToCart={(id) => handleAddToCart(null, id)}
    />
  ))}

            </div>

            {filteredAndSortedProducts.length === 0 && (
              <div className="text-center py-12 text-gray-600">
                <p className="text-lg mb-4">
                  {searchQuery ? `No products found for "${searchQuery}"` : "No products found matching your criteria."}
                </p>
                <button onClick={handleClearAllFilters} className="text-gray-800 hover:underline font-medium">
                  Clear all filters {searchQuery && "and search"}
                </button>
              </div>
            )}

            {totalPages > 1 && (
              <ProductPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                className="mt-8"
              />
            )}
          </div>
        </div>
      </div>
      <Sidebar isCartOpen={isCartOpen} onClose={closeCartSidebar} />
    </div>
  )
}